import React, { useState } from 'react';
import {
    Button, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Box, CircularProgress, Typography,
    FormControl, InputLabel, Select, MenuItem,
    Checkbox, FormControlLabel, ListItemText,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { createDQInsiderProfileService } from '../../../../services/dq_insider.service';
import { toastSuccess } from '../../../../utils/utils';
import { REGIONS } from '../../../../utils/META/cat_101';
import { countries as ISO_COUNTRIES } from '../../../../utils/countries';
import { ORGANIZATION_TYPE_OPTIONS, PRIMARY_ACTIVITY_OPTIONS } from '../../../../utils/constants';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Dialling code — '+' then digits, e.g. '+65' (PbDQ Data Collection Fields handover §5.4).
const DIAL_CODE_RE = /^\+\d{1,4}$/;

const EMPTY_FORM = {
    // Your information — required
    first_name: '',
    last_name: '',
    email: '',
    phone_country_code: '',
    phone_number: '',
    title: '',
    // Your organization — required unless noted
    business_name: '',
    business_type: '',              // organization type code
    primary_activities: [],
    country: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    website: '',                    // optional
    // Legal entity information — required
    legal_same_as_organization: false,
    registered_legal_name: '',
    registration_country: '',
    legal_same_as_organization_address: false,
    registered_address_line1: '',
    registered_address_line2: '',   // optional
    registered_city: '',
    registered_state: '',
    registered_postal_code: '',
    // Enquiry details — optional, from the old /pbdq/apply enquiry form
    num_children: '',
    num_parents: '',
    num_teachers: '',
    num_schools: '',
    num_companies: '',
    rollout_period: '',
    translations_needed: '',
    translations_language: '',
    message: '',
};

// Defeat Chrome's cross-field autofill (so opening this dialog does not prefill
// the "Search Email / Name" box on the page behind it, and vice-versa).
const NO_AUTOFILL = { autoComplete: 'off' };

// Band / roll-out options — same as the public /pbdq/apply enquiry form.
const COUNT_BANDS = [
    { value: '', label: 'Select…' },
    { value: 'under_100', label: 'Under 100' },
    { value: '100_500', label: '100 – 500' },
    { value: '500_1000', label: '500 – 1,000' },
    { value: '1000_5000', label: '1,000 – 5,000' },
    { value: '5000_10000', label: '5,000 – 10,000' },
    { value: '10000_plus', label: '10,000+' },
];
const ROLLOUT_OPTIONS = [
    { value: '', label: 'Select…' },
    { value: 'immediate', label: 'Immediately' },
    { value: '1_3_months', label: '1 – 3 months' },
    { value: '3_6_months', label: '3 – 6 months' },
    { value: '6_12_months', label: '6 – 12 months' },
    { value: '12_plus_months', label: '12+ months' },
];
// Organization country — cat_101 REGIONS, stored by name, as the profile's
// `country` always has been. Rendered as a type-to-search Autocomplete: the list
// runs to ~200 entries.
const COUNTRY_OPTIONS = REGIONS;
// Country of registration — stored as the ISO 3166-1 alpha-2 code, e.g. 'SG'
// (PbDQ Data Collection Fields handover §5.4).
const REGISTRATION_COUNTRY_OPTIONS = ISO_COUNTRIES.map((c) => ({ value: c.code, label: c.label }));

const ACTIVITY_LABEL = PRIMARY_ACTIVITY_OPTIONS.reduce((m, o) => ({ ...m, [o.value]: o.label }), {});

// Required fields — the sign-up chatbot's partner registry (DP-6), limited to
// fields the partner profile already has. The legal
// entity's name and address are checked on their effective values, so the
// "same as the organization" boxes satisfy them.
const REQUIRED_KEYS = [
    'first_name', 'last_name', 'phone_country_code', 'phone_number', 'title',
    'business_name', 'business_type', 'country', 'address', 'city', 'state', 'postal_code',
    'registration_country',
];

/** The legal entity as it will be sent, with the "same as" boxes applied. */
const legalEntityOf = (form) => ({
    registered_legal_name: form.legal_same_as_organization ? form.business_name : form.registered_legal_name,
    ...(form.legal_same_as_organization_address ? {
        registered_address_line1: form.address,
        registered_address_line2: '',
        registered_city: form.city,
        registered_state: form.state,
        registered_postal_code: form.postal_code,
    } : {
        registered_address_line1: form.registered_address_line1,
        registered_address_line2: form.registered_address_line2,
        registered_city: form.registered_city,
        registered_state: form.registered_state,
        registered_postal_code: form.registered_postal_code,
    }),
});

const SectionTitle = ({ children }) => (
    <Typography variant="subtitle2" style={{ marginTop: 20, marginBottom: 4, fontWeight: 700 }}>
        {children}
    </Typography>
);

/**
 * "Add Insider" button + dialog for the PBDQS List tab.
 *
 * Creates a DQ Pro partner's DQ Insider account. POSTs to
 * /admin/dq_insider/profiles/create. On success it calls `onCreated` so the
 * parent list refreshes and shows the new account.
 *
 * The fields are the sign-up chatbot's partner registry (DP-6) — Your
 * information, Your organization, Legal entity information — with the same
 * required set, limited to fields the partner profile already has (organization
 * type goes to `business_type`; the office address is one line; no "please
 * specify" text or registration number). Organization type and primary
 * activities are sent as the platform codes. The old /pbdq/apply enquiry questions follow as optional
 * extras (stored in the profile's additional details).
 *
 * Props: { onCreated?: () => void }
 */
const CreateInsiderButton = ({ onCreated }) => {
    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);

    // Read value before the state updater runs (React 16 pools synthetic events).
    const setField = (key) => (e) => {
        const { value } = e.target;
        setForm((f) => ({ ...f, [key]: value }));
    };
    const setChecked = (key) => (e) => {
        const { checked } = e.target;
        setForm((f) => ({ ...f, [key]: checked }));
    };

    const text = (label, key, { required = true, ...rest } = {}) => (
        <TextField
            label={label} required={required} fullWidth margin="dense"
            value={form[key]} onChange={setField(key)} disabled={submitting}
            inputProps={NO_AUTOFILL}
            {...rest}
        />
    );

    const renderSelect = (label, key, options, required = false) => (
        <FormControl fullWidth margin="dense" required={required} disabled={submitting}>
            <InputLabel>{label}</InputLabel>
            <Select value={form[key]} onChange={setField(key)}>
                {options.map((o) => (
                    <MenuItem key={o.value || 'none'} value={o.value}>{o.label}</MenuItem>
                ))}
            </Select>
        </FormControl>
    );

    const countrySelect = (label, key, options = COUNTRY_OPTIONS) => (
        <Autocomplete
            options={options}
            getOptionLabel={(o) => (o && o.label) || ''}
            getOptionSelected={(o, v) => o.value === (v && v.value)}
            value={options.find((o) => o.value === form[key]) || null}
            onChange={(_e, option) => setForm((prev) => ({ ...prev, [key]: option ? option.value : '' }))}
            disabled={submitting}
            autoHighlight
            openOnFocus
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    required
                    margin="dense"
                    placeholder="Type to search…"
                    inputProps={{ ...params.inputProps, autoComplete: 'off' }}
                />
            )}
        />
    );

    const emailValid = EMAIL_RE.test(form.email.trim());
    const dialCodeValid = DIAL_CODE_RE.test(form.phone_country_code.trim());
    const legal = legalEntityOf(form);
    const canSubmit =
        emailValid &&
        dialCodeValid &&
        REQUIRED_KEYS.every((k) => !!String(form[k]).trim()) &&
        form.primary_activities.length > 0 &&
        !!String(legal.registered_legal_name).trim() &&
        !!String(legal.registered_address_line1).trim() &&
        !!String(legal.registered_city).trim() &&
        !!String(legal.registered_state).trim() &&
        !!String(legal.registered_postal_code).trim() &&
        !submitting;

    const handleOpen = () => { setForm(EMPTY_FORM); setOpen(true); };
    const handleClose = () => { if (!submitting) setOpen(false); };

    const handleSubmit = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            const t = (v) => String(v || '').trim();
            const payload = {
                first_name: t(form.first_name),
                last_name: t(form.last_name),
                email: t(form.email),
                phone_country_code: t(form.phone_country_code),
                phone_number: t(form.phone_number),
                title: t(form.title),

                business_name: t(form.business_name),
                business_type: form.business_type,
                primary_activities: form.primary_activities,
                country: form.country,
                address: t(form.address),
                city: t(form.city),
                state: t(form.state),
                postal_code: t(form.postal_code),
                website: t(form.website),

                registered_legal_name: t(legal.registered_legal_name),
                registration_country: form.registration_country,
                registered_address_line1: t(legal.registered_address_line1),
                registered_address_line2: t(legal.registered_address_line2),
                registered_city: t(legal.registered_city),
                registered_state: t(legal.registered_state),
                registered_postal_code: t(legal.registered_postal_code),

                num_children: t(form.num_children),
                num_parents: t(form.num_parents),
                num_teachers: t(form.num_teachers),
                num_schools: t(form.num_schools),
                num_companies: t(form.num_companies),
                rollout_period: t(form.rollout_period),
                translations_needed: form.translations_needed,
                translations_language: t(form.translations_language),
                message: t(form.message),
            };
            const res = await createDQInsiderProfileService(payload);
            toastSuccess((res && res.message) || 'Insider account created.');
            setOpen(false);
            setForm(EMPTY_FORM);
            if (typeof onCreated === 'function') onCreated();
        } catch (err) {
            // invokeApi already shows the error toast — just log here (avoids a duplicate toast).
            console.error('[CreateInsider] create failed:', err);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleOpen}
                style={{ textTransform: 'none' }}
            >
                Add Insider
            </Button>

            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>Add Insider</DialogTitle>
                {/* autoComplete="off" on the form + each field stops browser autofill */}
                <DialogContent dividers>
                    <form autoComplete="off">
                        <SectionTitle>Your information</SectionTitle>
                        <Box display="flex" style={{ gap: 12 }}>
                            {text('First Name', 'first_name')}
                            {text('Last Name', 'last_name')}
                        </Box>
                        {text('Business Email', 'email', {
                            type: 'email',
                            error: form.email.length > 0 && !emailValid,
                            helperText: form.email.length > 0 && !emailValid ? 'Enter a valid email address.' : '',
                        })}
                        <Box display="flex" style={{ gap: 12 }}>
                            <Box style={{ flex: '0 0 140px' }}>
                                {text('Dial Code', 'phone_country_code', {
                                    placeholder: '+65',
                                    error: form.phone_country_code.length > 0 && !dialCodeValid,
                                    helperText: form.phone_country_code.length > 0 && !dialCodeValid ? 'e.g. +65' : '',
                                })}
                            </Box>
                            {text('Phone Number', 'phone_number')}
                        </Box>
                        {text('Job Title / Role', 'title')}

                        <SectionTitle>Your organization</SectionTitle>
                        {text('Organization Name', 'business_name')}
                        {renderSelect('Organization Type', 'business_type', ORGANIZATION_TYPE_OPTIONS, true)}
                        <FormControl fullWidth margin="dense" required disabled={submitting}>
                            <InputLabel>Primary Role / Activity</InputLabel>
                            <Select
                                multiple
                                value={form.primary_activities}
                                onChange={setField('primary_activities')}
                                renderValue={(selected) => selected.map((v) => ACTIVITY_LABEL[v] || v).join(', ')}
                            >
                                {PRIMARY_ACTIVITY_OPTIONS.map((o) => (
                                    <MenuItem key={o.value} value={o.value}>
                                        <Checkbox checked={form.primary_activities.indexOf(o.value) > -1} size="small" />
                                        <ListItemText primary={o.label} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {countrySelect('Organization Country', 'country')}
                        {text('Office Address', 'address')}
                        <Box display="flex" style={{ gap: 12 }}>
                            {text('City', 'city')}
                            {text('State / Province / Region', 'state')}
                        </Box>
                        {text('Postal Code', 'postal_code')}
                        {text('Organization Website', 'website', { required: false, placeholder: 'https://' })}

                        <SectionTitle>Legal entity information</SectionTitle>
                        {!form.legal_same_as_organization && text('Registered Legal Entity Name', 'registered_legal_name')}
                        <FormControlLabel
                            control={<Checkbox checked={form.legal_same_as_organization} onChange={setChecked('legal_same_as_organization')} disabled={submitting} size="small" />}
                            label="Same as the organization name above"
                        />
                        {countrySelect('Country of Registration', 'registration_country', REGISTRATION_COUNTRY_OPTIONS)}
                        {!form.legal_same_as_organization_address && (
                            <>
                                {text('Registered Address Line 1', 'registered_address_line1')}
                                {text('Registered Address Line 2', 'registered_address_line2', { required: false })}
                                <Box display="flex" style={{ gap: 12 }}>
                                    {text('City', 'registered_city')}
                                    {text('State / Province / Region', 'registered_state')}
                                </Box>
                                {text('Postal Code', 'registered_postal_code')}
                            </>
                        )}
                        <FormControlLabel
                            control={<Checkbox checked={form.legal_same_as_organization_address} onChange={setChecked('legal_same_as_organization_address')} disabled={submitting} size="small" />}
                            label="Same as the organization address above"
                        />

                        <SectionTitle>Enquiry details (optional)</SectionTitle>
                        <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>
                            How many people do you intend to assess, and over what roll-out period?
                        </Typography>
                        <Box display="flex" style={{ gap: 12 }}>
                            {renderSelect('# of children you intend to assess', 'num_children', COUNT_BANDS)}
                            {renderSelect('# of parents you intend to assess', 'num_parents', COUNT_BANDS)}
                        </Box>
                        <Box display="flex" style={{ gap: 12 }}>
                            {renderSelect('# of teachers you intend to assess', 'num_teachers', COUNT_BANDS)}
                            {renderSelect('# of schools you intend to assess', 'num_schools', COUNT_BANDS)}
                        </Box>
                        {renderSelect('# of companies you intend to assess', 'num_companies', COUNT_BANDS)}
                        {renderSelect('When do you plan to roll this out?', 'rollout_period', ROLLOUT_OPTIONS)}

                        <FormControl fullWidth margin="dense">
                            <InputLabel>Do you need translations?</InputLabel>
                            <Select
                                value={form.translations_needed}
                                onChange={setField('translations_needed')}
                                disabled={submitting}
                            >
                                <MenuItem value=""><em>No</em></MenuItem>
                                <MenuItem value="Yes">Yes</MenuItem>
                            </Select>
                        </FormControl>
                        {form.translations_needed === 'Yes' && text('If so, which language(s)?', 'translations_language', { required: false })}

                        {text("Anything else you'd like us to know?", 'message', { required: false, multiline: true, rows: 2 })}
                    </form>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        color="primary"
                        variant="contained"
                        disabled={!canSubmit}
                        startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        Create
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CreateInsiderButton;
